import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Moon,
  Users,
  PhoneIncoming,
  PhoneForwarded,
  ArrowLeftRight,
  Hash,
  Music2,
  ListTree,
  UserCheck,
  GitBranch,
  PhoneOff,
  CheckCircle,
  Puzzle,
  Smartphone,
  Monitor,
  HeadphonesIcon,
  Radio,
  Headphones,
  ShieldCheck,
  Mic,
  Building2,
  Phone,
} from "lucide-react";
import heroBg from "@/assets/telefonie-functies-bg.jpg";

const features = [
  {
    icon: Moon,
    name: "Nachtschakeling & openingstijden",
    tagline: "Altijd het juiste antwoord, ook als je niet werkt.",
    color: "bg-hero-gradient",
    bullets: [
      "Verschillende regels per dag en tijdslot",
      "Automatisch omschakelen — geen handmatige actie nodig",
      "Professionele berichtgeving buiten openingstijden",
      "Feestdagen apart instelbaar",
      "Vakantie- en feestdagenrooster",
      "Aan / Uit / Op tijdschema",
    ],
    benefit: "Jij slaapt goed, bellers worden netjes geholpen.",
  },
  {
    icon: ListTree,
    name: "Keuzemenu (IVR)",
    tagline: "\"Toets 1 voor verkoop…\" — direct bij de juiste persoon.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Enkel of meerlaags keuzemenu (tot 12 opties per laag)",
      "Apart keuzemenu voor binnen en buiten werktijd",
      "Eigen welkomstboodschap of tekst-naar-spraak",
      "Eenvoudig aanpasbaar via het portaal",
      "Fallback naar voicemail of wachtrij",
    ],
    benefit: "Minder doorverbinden, sneller geholpen.",
  },
  {
    icon: Users,
    name: "Belgroepen",
    tagline: "Het hele team bereikbaar via één nummer.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Extern en/of intern nummer koppelen aan een groep",
      "Gelijktijdig, in vaste volgorde of op basis van gesprekshistorie",
      "Eenvoudig gebruikers toevoegen of verwijderen",
      "Meerdere groepen mogelijk — per afdeling of project",
      "Fallback als niemand opneemt",
    ],
    benefit: "Altijd iemand beschikbaar, geen gemiste gesprekken.",
  },
  {
    icon: PhoneForwarded,
    name: "Doorschakelen",
    tagline: "Jouw nummer volgt jou overal.",
    color: "bg-hero-gradient",
    bullets: [
      "Direct doorschakelen of na X seconden",
      "Doorschakelen bij bezet, geen gehoor of geen internet",
      "Naar mobiel, vast nummer of collega",
      "Per gebruiker instelbaar via het portaal",
      "Selectief doorschakelen op basis van tijdschema",
    ],
    benefit: "Altijd bereikbaar, van waar je ook werkt.",
  },
  {
    icon: ArrowLeftRight,
    name: "Doorverbinden & wisselgesprek",
    tagline: "Net zoals bij grote bedrijven — maar dan betaalbaar.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Blind doorverbinden (direct) of met ruggespraak",
      "Doorverbinden naar ieder gewenst telefoonnummer",
      "Wisselgesprek: twee of drie gesprekken beheren",
      "Deelnemers wisselgesprek met elkaar doorverbinden",
      "Werkt op desk phones, softphones en mobiel",
    ],
    benefit: "Professioneel communiceren binnen je team.",
  },
  {
    icon: Hash,
    name: "Nummerweergave",
    tagline: "Jij bepaalt welk nummer de beller ziet.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Kies welk uitgaand nummer zichtbaar is",
      "Naamweergave bij inkomende oproepen",
      "Naamweergave van belgroepen",
      "Per gebruiker of per afdeling instelbaar",
      "Werkt ook bij doorgeschakelde gesprekken",
    ],
    benefit: "Klanten herkennen je nummer — en nemen eerder op.",
  },
  {
    icon: Music2,
    name: "Wachtrij & wachtmuziek",
    tagline: "Bellers in de rij — professioneel en geduldig.",
    color: "bg-hero-gradient",
    bullets: [
      "Wachtrijen tot 500+ wachtenden",
      "Zelf te kiezen wachtmuziek of informatieve tekst",
      "Iedere afdeling eigen wachtmuziek",
      "Positie in de rij of resterende wachttijd doorgeven",
      "Maximum wachttijd met automatische fallback",
      "Medewerkers kunnen zichzelf aan- en afmelden",
    ],
    benefit: "Minder afhaken, meer gesprekken afgerond.",
  },
  {
    icon: PhoneIncoming,
    name: "Voicemail naar e‑mail",
    tagline: "Gemiste berichten komen direct in je inbox.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Audiobestand direct als e-mailbijlage",
      "Nummerweergave in onderwerp van de e-mail",
      "Doorsturen naar willekeurige e-mailadressen",
      "Optioneel ook tekst-transcriptie van het bericht",
      "Berichten worden ook bewaard in het portaal",
    ],
    benefit: "Nooit meer een gemist bericht — altijd snel reageren.",
  },
  {
    icon: UserCheck,
    name: "Status & bereikbaarheid",
    tagline: "Weet direct wie er beschikbaar is.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Realtime aanwezigheidsstatus per collega",
      "Beschikbaar, bezet, niet storen of afwezig",
      "Eenvoudig gesprekken overnemen via het systeem",
      "Werkt op desk phones en softphones",
      "Organisatiebreed te bekijken bij multicentrale",
    ],
    benefit: "Betere samenwerking, minder gemiste gesprekken.",
  },
  {
    icon: GitBranch,
    name: "Conferentiegesprek",
    tagline: "Meerdere mensen tegelijk aan de lijn.",
    color: "bg-hero-gradient",
    bullets: [
      "Conferentiegesprek met 3 of meer personen",
      "Locatie onafhankelijk — intern en extern",
      "Starten vanuit een bestaand gesprek",
      "Deelnemers kunnen in- en uitstappen",
      "Vergaderen met beeld en/of geluid",
      "Bureaublad delen met andere deelnemers",
    ],
    benefit: "Overleg met iedereen tegelijk — waar ook ter wereld.",
  },
  {
    icon: PhoneOff,
    name: "Niet storen",
    tagline: "Even focussen zonder onderbrekingen.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Met één klik activeren op je toestel",
      "Automatische doorschakeling bij niet storen",
      "Tijdelijk of permanent instelbaar",
      "Bellers merken niets van de omschakeling",
      "Collega's kunnen je status direct zien",
    ],
    benefit: "Ongestoord werken terwijl bellers toch goed geholpen worden.",
  },
  {
    icon: Radio,
    name: "Oproep overname",
    tagline: "Neem de telefoon van een collega over.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Neem eenvoudig een rinkelde telefoon van collega over",
      "Verschillende overname-groepen per afdeling",
      "Opname via *-code of sneltoets op toestel",
      "Ook mobiele gebruikers kunnen gesprekken overnemen",
    ],
    benefit: "Nooit meer een gesprek missen als een collega even weg is.",
  },
  {
    icon: Smartphone,
    name: "Vast-Mobiel Integratie (VAMO)",
    tagline: "Vaste telefoonfuncties op je mobiel.",
    color: "bg-hero-gradient",
    bullets: [
      "Verbeter de bereikbaarheid van je organisatie",
      "Vaste toestelfuncties beschikbaar op mobiel",
      "Werken vanaf iedere locatie mogelijk",
      "Doorverbinden met ruggespraak ook op mobiel",
      "Deelnemen aan wachtrijen vanaf je mobiel",
    ],
    benefit: "Eén nummer, altijd bereikbaar — op kantoor of onderweg.",
  },
  {
    icon: Monitor,
    name: "Computer integratie (CTI)",
    tagline: "Klik en bel direct vanuit je computer.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Klik en bel vanaf webpagina's en vanuit programma's",
      "Beschikbaarheid en gespreksgeschiedenis direct zichtbaar",
      "Ideaal voor een kleine receptie",
      "Koppeling met CRM voor automatische klantherkenning",
    ],
    benefit: "Sneller bellen, minder fouten, beter klantcontact.",
  },
  {
    icon: Puzzle,
    name: "CRM & ERP koppeling",
    tagline: "Koppel je telefonie aan je bedrijfssoftware.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Koppeling met ruim 350 CRM en ERP pakketten",
      "Automatische klantherkenning bij inkomende gesprekken",
      "Verhoogt klantgerichtheid en klanttevredenheid",
      "Gesprekshistorie direct in je CRM zichtbaar",
    ],
    benefit: "Je team weet altijd wie er belt — vóórdat ze opnemen.",
  },
  {
    icon: Headphones,
    name: "Receptie software",
    tagline: "Professioneel gespreksbeheer voor elke receptioniste.",
    color: "bg-hero-gradient",
    bullets: [
      "Gesprekken en wachtrijen overzichtelijk beheren",
      "Locatieonafhankelijk — werkt in elke browser",
      "Eenvoudig doorverbinden en parkeren",
      "Realtime overzicht van beschikbaarheid collega's",
    ],
    benefit: "Eén receptioniste beheert alle gesprekken — van overal.",
  },
  {
    icon: HeadphonesIcon,
    name: "Callcenter software",
    tagline: "Geavanceerde gespreksverdeling voor drukke teams.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Gespreksverdeling op basis van skills of prioriteiten",
      "Uitgebreide rapportages per wachtrij en medewerker",
      "Werkt op ieder device met internet en browser",
      "Medewerkers zelf aan- en afmelden voor wachtrijen",
    ],
    benefit: "Maximale efficiëntie voor teams met veel inbound verkeer.",
  },
  {
    icon: Mic,
    name: "Gespreksopname",
    tagline: "Elk gesprek bewaard — voor training of juridische zekerheid.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Gesprekken van vaste en mobiele telefoons opnemen",
      "Opnamen tot 365 dagen veilig bewaard",
      "Gesprekken downloaden of terugluisteren via webportal",
      "Uitgebreide zoek- en filtermogelijkheden",
      "Opnames opgeslagen op servers in Nederland",
    ],
    benefit: "Altijd bewijs bij hand — en een scherp inwerkprogramma.",
  },
  {
    icon: Building2,
    name: "Multicentrale",
    tagline: "Meerdere vestigingen, één telefonienetwerk.",
    color: "bg-hero-gradient",
    bullets: [
      "Ideaal voor organisaties met meerdere vestigingen",
      "Eenvoudig intern doorverbinden tussen vestigingen",
      "Bij storing op één locatie neemt een andere het over",
      "Organisatiebrede centrale receptie mogelijk",
      "Per vestiging aparte factuur en nummerplan",
    ],
    benefit: "Alle vestigingen als één professioneel bedrijf naar buiten.",
  },
  {
    icon: ShieldCheck,
    name: "Storingsbestendig bellen",
    tagline: "Altijd bereikbaar — ook als je internet hapert.",
    color: "bg-rinqel-blue-gradient",
    bullets: [
      "Bij internetstoring automatisch doorschakelen naar mobiel",
      "Gesprekken verplaatsen naar andere vestiging",
      "Klanten merken niets van een storing aan jouw kant",
      "Servers drievoudig uitgevoerd op 3 fysiek andere locaties",
      "Gegarandeerde uptime van meer dan 99%",
    ],
    benefit: "Jij bent bereikbaar. Punt.",
  },
  {
    icon: Phone,
    name: "Flexplek bellen",
    tagline: "Log in op elk toestel en het wordt direct van jou.",
    color: "bg-rinqel-green-gradient",
    bullets: [
      "Werken met eigen nummer vanaf iedere flexwerkplek",
      "Inloggen met gebruikersnaam en wachtwoord",
      "Gebruikersinstellingen automatisch geladen uit de cloud",
      "Bij inloggen elders automatisch uitloggen op ander toestel",
    ],
    benefit: "Thuis, op kantoor of ergens anders — altijd je eigen toestel.",
  },
];

const TelefonieFeatures = () => {
  return (
    <>
      {/* Hero — zelfde stijl als telefonie-hoofdpagina */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={heroBg}
          alt="Zakelijke telefonie functies"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />

        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/telefonie"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Telefonie
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Maandelijks opzegbaar · Geen kleine lettertjes
              </span>
            </motion.div>

            <motion.h1
              className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Alle functies van{" "}
              <span className="text-gradient">Rinqel Telefonie</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Elke functie is ontworpen met één doel: zorgen dat jij professioneel bereikbaar bent, zonder gedoe.
              Alle functies zijn inbegrepen — geen verborgen kosten.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {features.length} functies inbegrepen
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Dagelijks aanpasbaar
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                99%+ uptime
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature cards grid — 3 per rij */}
      <section className="py-16 md:py-24 bg-hero-gradient-subtle">
        <div className="container">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              const colColor = i % 3 === 0
                ? "bg-hero-gradient"
                : i % 3 === 1
                ? "bg-rinqel-blue-gradient"
                : "bg-rinqel-green-gradient";
              return (
                <div
                  key={f.name}
                  className="flex flex-col rounded-2xl border border-border bg-card p-7 shadow-card"
                >
                  {/* Icon */}
                  <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${colColor}`}>
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>

                  {/* Title & tagline */}
                  <h2 className="text-lg font-bold">{f.name}</h2>
                  <p className="mt-1 text-sm font-medium text-primary">{f.tagline}</p>

                  {/* Bullets */}
                  <ul className="mt-4 flex-1 space-y-2">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                        <span className="text-muted-foreground">{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Benefit */}
                  <div className={`mt-5 rounded-xl p-4 ${colColor}`}>
                    <p className="text-sm font-semibold text-white">✓ {f.benefit}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            className="mt-16 rounded-2xl border border-border bg-card p-10 text-center shadow-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-bold">Klaar om professioneler te bellen?</h3>
            <p className="mt-2 text-muted-foreground">Maandelijks opzegbaar, dagelijks aanpasbaar. Geen gedoe.</p>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-functies-cta">
                  Bereken je prijs <ArrowRight className="h-4 w-4" />
                </TrackedLink>
              </Button>
              <Button variant="hero-outline" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="telefonie-functies-cta">Neem contact op</TrackedLink>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default TelefonieFeatures;
